import type { ChatMessage, ChatStatus } from "./types.js";
import type { JonasSearchResultDto } from "./api-client.js";

export function getLiveFallbackMessage(
  messages: ChatMessage[],
): ChatMessage | null {
  const last = messages[messages.length - 1];
  return last?.fallback && last.fallback.frozen === false ? last : null;
}

export function isThreadInFallbackMode(messages: ChatMessage[]): boolean {
  return getLiveFallbackMessage(messages) !== null;
}

/**
 * True while the AI Platform has recovered and its replacement answer for the
 * original failed question is streaming in. A reconnect appends a pending
 * assistant message AFTER the still-live fallback element, so the thread is no
 * longer "in fallback mode" (the fallback is not the trailing message) yet a
 * `frozen === false` fallback is still present while its answer streams. This is
 * the window in which the recovery (green) banner and the "re-asking" notice are
 * shown; it ends the instant the answer completes and the fallback freezes.
 */
export function isReconnectingAfterFallback(
  messages: ChatMessage[],
  status: ChatStatus,
): boolean {
  if (status !== "starting" && status !== "streaming") return false;
  return messages.some(
    (message) => message.fallback && message.fallback.frozen === false,
  );
}

export function mapJonasResultsToSources(
  results: JonasSearchResultDto[],
): unknown[] {
  return results
    .filter((result) => result.url && result.url !== "#")
    .slice(0, 5)
    .map((result) => ({
      kind: "help-article",
      url: result.url,
      title: result.title,
      label: result.title,
      excerpt: normalizePreviewText(result.description),
      sourcetag: result.type,
      opensInNewTab: false,
    }));
}

/**
 * Turns a Jonas `description` (the article's normalized markdown text) into a
 * clean plain-text preview. Jonas repeats the article title as a leading
 * markdown heading and the body contains markdown markers; without this the
 * excerpt echoes the title and shows raw markdown. Mirrors the original
 * `normalizePreviewText` behavior from the legacy Help.js search results.
 */
export function normalizePreviewText(text: string): string {
  if (!text) return "";
  // Drop the whole leading heading line (the repeated article title).
  const withoutHeading = text.replace(/^\s*#{1,6}[ \t]+[^\n]*(?:\r?\n)+/, "");
  return stripMarkdown(withoutHeading);
}

/**
 * Strips markdown markers from a user question so the fallback Jonas search
 * receives a plain-text query. User questions are authored in the markdown-aware
 * composer and kept as raw markdown for display; the search term must not carry
 * markers such as **, #, or [label](url) into Jonas. Reuses the same
 * stripMarkdown as the preview normalizer so there is a single markdown-stripping
 * source of truth. Unlike normalizePreviewText it does NOT drop a leading heading
 * line — a question is not a Jonas article with a repeated title.
 */
export function stripMarkdownForSearch(text: string): string {
  return stripMarkdown(text);
}

function stripMarkdown(text: string): string {
  if (!text) return "";
  return text
    .replace(/^[ \t]*#{1,6}[ \t]+/gm, "") // heading markers (keep their text)
    .replace(/(^|\s)[-*+](?=\s)/g, "$1") // bullet markers (line-start or inline)
    .replace(/^[ \t]*\d+\.[ \t]+/gm, "") // ordered markers
    .replace(/\*\*(.+?)\*\*/g, "$1") // bold
    .replace(/\*(.+?)\*/g, "$1") // italic
    .replace(/__(.+?)__/g, "$1") // bold (underscore)
    .replace(/_(.+?)_/g, "$1") // italic (underscore)
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, "") // images (dropped)
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // links -> text
    .replace(/`([^`]+)`/g, "$1") // inline code
    .replace(/\|/g, " ") // table pipes
    .replace(/-{3,}/g, "") // hr / table rule
    .replace(/\r\n|\n|\r/g, " ") // newlines -> space
    .replace(/\s+/g, " ") // collapse whitespace
    .trim();
}
